import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import TeesSubNav from '../components/TeesSubNav';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO = 'https://daily-obx-cdn.b-cdn.net/artwork/beachbumtees-logo.jpg';

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('tees') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

// Default category images (can be overridden by product images)
const DEFAULT_CATEGORY_IMAGES = {
  'short-sleeve': null,
  'long-sleeve': 'https://daily-obx-cdn.b-cdn.net/newsletter-images/longsleeve%20tee.jpg',
  'new-designs': null,
  'caps': null,
  'hoodies': null,
  'tank-tops': null
};

function Tees() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { itemCount, openCart } = useCart();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tees/categories`);
      setCategories(response.data);
    } catch (error) {
      // Fallback to static categories if API fails
      setCategories([
        { id: 'short-sleeve', label: 'Short Sleeve', count: 0, image_url: DEFAULT_CATEGORY_IMAGES['short-sleeve'] },
        { id: 'long-sleeve', label: 'Long Sleeve', count: 0, image_url: DEFAULT_CATEGORY_IMAGES['long-sleeve'] },
        { id: 'new-designs', label: 'New Designs', count: 0, image_url: DEFAULT_CATEGORY_IMAGES['new-designs'] },
        { id: 'caps', label: 'Headwear', count: 0, image_url: DEFAULT_CATEGORY_IMAGES['caps'] },
        { id: 'hoodies', label: 'Hoodies', count: 0, image_url: DEFAULT_CATEGORY_IMAGES['hoodies'] },
        { id: 'tank-tops', label: 'Tank Tops', count: 0, image_url: DEFAULT_CATEGORY_IMAGES['tank-tops'] }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      <TeesSubNav />

      {/* Hero Section */}
      <section
        className="tees-hero"
        style={{
          background: '#fff',
          padding: '50px 20px',
          textAlign: 'center'
        }}
      >
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <img
            src={LOGO}
            alt="Beach Bum Tees - tees, tanks, tiedyes, hoodies, caps, other cool stuff"
            style={{ maxWidth: '550px', width: '90%', height: 'auto', display: 'block', margin: '0 auto' }}
          />
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          marginTop: '36px',
          flexWrap: 'wrap'
        }}>
          <Link
            to="/shop/tees/men"
            style={{
              padding: '14px 36px',
              background: '#1a1a1a',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontWeight: '600'
            }}
          >
            Shop Men
          </Link>
          <Link
            to="/shop/tees/women"
            style={{
              padding: '14px 36px',
              background: '#1a1a1a',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontWeight: '600'
            }}
          >
            Shop Women
          </Link>
          <Link
            to="/shop/tees/kids"
            style={{
              padding: '14px 36px',
              background: '#fff',
              color: '#1a1a1a',
              textDecoration: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontWeight: '600',
              border: '1px solid #1a1a1a'
            }}
          >
            Shop Kids
          </Link>
        </div>
      </section>

      {/* Main Content - Category Cards */}
      <main style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '60px 20px 80px' 
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="loading-spinner" />
          </div>
        ) : (
          <>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '32px',
              textAlign: 'center',
              marginBottom: '50px',
              color: '#1a1a1a'
            }}>
              Shop By Category
            </h2>

            <div className="tees-categories-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: '25px'
            }}>
              {categories.map(category => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>

            <style>{`
              @media (max-width: 1200px) {
                .tees-categories-grid {
                  grid-template-columns: repeat(3, 1fr) !important;
                }
              }
              @media (max-width: 768px) {
                .tees-categories-grid {
                  grid-template-columns: repeat(2, 1fr) !important;
                }
              }
              @media (max-width: 480px) {
                .tees-categories-grid {
                  grid-template-columns: 1fr !important;
                }
              }
              .category-card-hover:hover {
                transform: translateY(-5px);
              }
            `}</style>
          </>
        )}
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />
    </div>
  );
}

// Category Card Component
function CategoryCard({ category }) {
  const hasProducts = category.count > 0;
  const imageUrl = category.image_url || DEFAULT_CATEGORY_IMAGES[category.id];
  
  const cardContent = (
    <div 
      style={{
        width: '100%',
        cursor: hasProducts ? 'pointer' : 'default',
        transition: 'transform 0.2s',
        opacity: hasProducts ? 1 : 0.6
      }}
      className={hasProducts ? 'category-card-hover' : ''}
      data-testid={`category-${category.id}`}
    >
      {/* Image Container */}
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        marginBottom: '12px'
      }}>
        <div style={{
          aspectRatio: '3/4',
          overflow: 'hidden',
          background: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={category.label}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#bbb',
              padding: '20px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '40px', marginBottom: '8px' }}>
                {category.id === 'caps' ? '🧢' :
                 category.id === 'hoodies' ? '🧥' :
                 category.id === 'tank-tops' ? '👚' :
                 category.id === 'new-designs' ? '✨' : '👕'}
              </span>
              <span style={{ fontSize: '12px' }}>Coming Soon</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Label */}
      <h3 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '18px',
        fontWeight: '400',
        textAlign: 'center',
        margin: 0,
        color: '#1a1a1a'
      }}>
        {category.label}
      </h3>
      
      {category.count > 0 && (
        <p style={{
          textAlign: 'center',
          color: '#888',
          fontSize: '13px',
          margin: '4px 0 0'
        }}>
          {category.count} {category.count === 1 ? 'item' : 'items'}
        </p>
      )}
    </div>
  );

  if (hasProducts) {
    return (
      <Link 
        to={`/shop/tees/${category.id}`} 
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

export default Tees;
